import { Column, FilterSpec } from '../types'

const isEmpty = (v: any) =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0)

const toNum = (v: any) => {
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

const toStr = (v: any) => (v === null || v === undefined ? '' : String(v))
const normalize = (s: string) => s.toLowerCase()

const getValue = <T,>(row: T, col: Column<T>) => {
  if (col.accessor) return col.accessor(row)
  return (row as any)[col.key]
}

export const applyFilters = <T,>(rows: T[], columns: Column<T>[], filters: FilterSpec[]) => {
  if (!filters || filters.length === 0) return rows
  const colMap = new Map(columns.map(c => [c.key, c] as const))

  return rows.filter((row) => {
    for (const f of filters) {
      const col = colMap.get(f.key)
      if (!col) continue
      const raw = getValue(row, col)

      const fOp = f.op as any;

      if (fOp === 'is_empty') {
        if (!isEmpty(raw)) return false
        continue
      }

      if (fOp === 'not_empty') {
        if (isEmpty(raw)) return false
        continue
      }

      if (fOp === 'is_true') {
        if (raw !== true) return false
        continue
      }

      if (fOp === 'is_false') {
        if (raw !== false) return false
        continue
      }

      if (col.type === 'boolean') {
        const a = !!raw;
        const b = typeof f.value === 'boolean' ? f.value : f.value === 'true';
        if (fOp === 'eq' && a !== b) return false;
        if (fOp === 'neq' && a === b) return false;
        continue;
      }

      if (col.type === 'number' || col.type === 'currency') {
        const a = toNum(raw)
        if (fOp === 'between') {
          const lo = toNum(f.value)
          const hi = toNum(f.value2)
          if (a === null || lo === null || hi === null) return false
          if (a < lo || a > hi) return false
          continue
        }

        const b = toNum(f.value)
        if (a === null || b === null) return false

        if (fOp === 'eq' && a !== b) return false
        if (fOp === 'neq' && a === b) return false
        if (fOp === 'gt' && !(a > b)) return false
        if (fOp === 'gte' && !(a >= b)) return false
        if (fOp === 'lt' && !(a < b)) return false
        if (fOp === 'lte' && !(a <= b)) return false

        continue
      }

      const s = normalize(toStr(raw))
      const v = normalize(toStr(f.value))

      if (fOp === 'eq' && s !== v) return false
      if (fOp === 'neq' && s === v) return false
      if (fOp === 'contains' && !s.includes(v)) return false
      if (fOp === 'not_contains' && s.includes(v)) return false
      if (fOp === 'starts_with' && !s.startsWith(v)) return false
      if (fOp === 'ends_with' && !s.endsWith(v)) return false

      if (fOp === 'in') {
        const arr = Array.isArray(f.value) ? f.value : [f.value]
        const set = new Set(arr.map((x: any) => normalize(toStr(x))))
        if (!set.has(s)) return false
      }

      if (fOp === 'not_in') {
        const arr = Array.isArray(f.value) ? f.value : [f.value]
        const set = new Set(arr.map((x: any) => normalize(toStr(x))))
        if (set.has(s)) return false
      }
    }
    return true
  })
}
